import type { Address } from 'viem';

import { erc20Abi, marketAbi } from '../shared/abi';
import { getMarketHistory, type MarketHistoryPoint } from '../shared/analytics';
import { getCache, setCache } from '../shared/cache';
import {
  defaultCollateralMeta,
  fmtCollateral,
  fmtCollateralUsd,
  resolveCollateralMeta,
  resolveTokenLogoUrl,
  type CollateralMeta,
} from '../shared/collateral';
import { readClient, readContractsBatch } from '../shared/client';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey, setActiveNetwork, type NetworkKey } from '../shared/config';
import { shortAddr } from '../shared/format';
import { createAnalyticsBucketsService, type AnalyticsRange } from '../market/analyticsBuckets';
import { renderBarHistorySvg, renderFlowSplitSvg, renderStackedFlowSvg } from '../market/analyticsRenderers';

const INFO_REFRESH_VISIBLE_MS = 45_000;
const INFO_REFRESH_HIDDEN_MS = 120_000;
const REFRESH_ICON_IMG = `<img src="${import.meta.env.BASE_URL}images/refreshing.svg" alt="" aria-hidden="true" style="width:18px; height:18px; display:block; filter:brightness(0) invert(1);" />`;

function normalizeOutcomeLabel(raw: unknown, fallback: string): string {
  const value = String(raw || '').trim();
  if (!value) return fallback;
  return value.slice(0, 48);
}

function renderSummaryPills(items: Array<{ label: string; value: string; tone?: string }>): string {
  return `
    <div class="analytics-summary-strip">
      ${items.map((item) => `
        <div class="analytics-summary-chip" style="--tone:${item.tone || '#dff7ff'};">
          <div class="mono analytics-summary-label">${item.label}</div>
          <div class="analytics-summary-value">${item.value}</div>
        </div>
      `).join('')}
    </div>
  `;
}

function renderTopTrades(history: MarketHistoryPoint[], formatValue: (value: bigint) => string, limit = 5): string {
  if (!history.length) return '';
  const rowCount = Math.max(1, limit);

  const topYes = [...history]
    .filter((point) => point.yesBuyFlow > 0n || point.yesSellFlow > 0n)
    .map((point) => ({
      action: point.yesBuyFlow > 0n ? 'Buy' : 'Sell',
      volume: point.yesBuyFlow > 0n ? point.yesBuyFlow : point.yesSellFlow,
    }))
    .sort((a, b) => (a.volume > b.volume ? -1 : a.volume < b.volume ? 1 : 0))
    .slice(0, rowCount);

  const topNo = [...history]
    .filter((point) => point.noBuyFlow > 0n || point.noSellFlow > 0n)
    .map((point) => ({
      action: point.noBuyFlow > 0n ? 'Buy' : 'Sell',
      volume: point.noBuyFlow > 0n ? point.noBuyFlow : point.noSellFlow,
    }))
    .sort((a, b) => (a.volume > b.volume ? -1 : a.volume < b.volume ? 1 : 0))
    .slice(0, rowCount);

  const maxYes = topYes.reduce((acc, item) => (item.volume > acc ? item.volume : acc), 1n);
  const maxNo = topNo.reduce((acc, item) => (item.volume > acc ? item.volume : acc), 1n);
  const yesRowsData = Array.from({ length: rowCount }, (_, index) => topYes[index] ?? { action: '', volume: 0n });
  const noRowsData = Array.from({ length: rowCount }, (_, index) => topNo[index] ?? { action: '', volume: 0n });

  const yesRows = yesRowsData.map((item) => {
    const widthPct = item.volume > 0n ? Math.max(8, Math.round((Number(item.volume) / Number(maxYes)) * 100)) : 0;
    const label = item.volume > 0n
      ? `<span class="analytics-top-trade-action-word ${item.action === 'Sell' ? 'is-sell' : 'is-buy'}"><span class="analytics-top-trade-action-text">${item.action}</span></span> <span class="analytics-top-trade-amount">${formatValue(item.volume)}</span>`
      : '--';
    const dotClass = item.action === 'Sell' ? 'is-sell' : 'is-buy';
    return `
      <div class="analytics-top-trade-side-row is-yes">
        <div class="analytics-top-trade-track is-yes">
          <div class="analytics-top-trade-fill is-yes" style="width:${widthPct}%"></div>
          <div class="mono analytics-top-trade-inline-label is-yes"><span class="analytics-top-trade-dot ${dotClass}" aria-hidden="true"></span>${label}</div>
        </div>
      </div>
    `;
  }).join('');

  const noRows = noRowsData.map((item) => {
    const widthPct = item.volume > 0n ? Math.max(8, Math.round((Number(item.volume) / Number(maxNo)) * 100)) : 0;
    const label = item.volume > 0n
      ? `<span class="analytics-top-trade-action-word ${item.action === 'Sell' ? 'is-sell' : 'is-buy'}"><span class="analytics-top-trade-action-text">${item.action}</span></span> <span class="analytics-top-trade-amount">${formatValue(item.volume)}</span>`
      : '--';
    const dotClass = item.action === 'Sell' ? 'is-sell' : 'is-buy';
    return `
      <div class="analytics-top-trade-side-row is-no">
        <div class="analytics-top-trade-track is-no">
          <div class="analytics-top-trade-fill is-no" style="width:${widthPct}%"></div>
          <div class="mono analytics-top-trade-inline-label is-no"><span class="analytics-top-trade-dot ${dotClass}" aria-hidden="true"></span>${label}</div>
        </div>
      </div>
    `;
  }).join('');

  return `
    <div class="analytics-top-trades analytics-top-trades-mirror" aria-label="Top outcome trades">
      <div class="analytics-top-trade-side is-yes">${yesRows}</div>
      <div class="analytics-top-trade-side is-no">${noRows}</div>
    </div>
  `;
}

export async function mountInfoView(appRoot: HTMLElement, marketAddress: Address): Promise<void> {
  if (!/^0x[0-9a-fA-F]{40}$/.test(marketAddress)) {
    throw new Error('Invalid market address for analytics view');
  }

  const activeChain = getActiveChainConfig();
  const activeNetwork = getActiveNetworkKey();
  const marketHref = `?market=${marketAddress}`;

  appRoot.innerHTML = `
    <main class="app" style="padding-bottom:120px; max-width:1180px;">
      <header class="topbar" style="margin-bottom:24px;">
        <div class="brand">
          <div class="brand-mark" aria-hidden="true"></div>
          <div>
            <div class="brand-text" style="font-size:1.1rem;">Market Analytics</div>
            <div class="brand-sub">${activeChain.chainName}</div>
          </div>
        </div>
        <div class="header-actions">
          <a class="btn header-back-btn" href="./" aria-label="Back to markets" title="Back to markets">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <a class="btn" href="${marketHref}">Back To Trade</a>
          <details class="header-dropdown" id="networkDropdown">
            <summary class="header-dropdown-btn" aria-label="Network selector">
              <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="9" fill="none" stroke-width="2"/>
                <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" fill="none" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span class="header-dropdown-caret" aria-hidden="true">▾</span>
            </summary>
            <div class="header-dropdown-menu" role="menu" aria-label="Network selector">
              <button id="networkMainnetBtn" class="header-dropdown-item ${activeNetwork === 'mainnet' ? 'active' : ''}" type="button" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2v20M2 12h20" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                  Mainnet
                </span>
              </button>
              <button id="networkTestnetBtn" class="header-dropdown-item ${activeNetwork === 'testnet' ? 'active' : ''}" type="button" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 6h16v12H4zM8 10h8" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Testnet
                </span>
              </button>
            </div>
          </details>
          <details class="header-dropdown" id="linksDropdown">
            <summary class="header-dropdown-btn" aria-label="Header links">
              <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke-width="2" stroke-linejoin="round"/>
                <path d="M4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="header-dropdown-caret" aria-hidden="true">▾</span>
            </summary>
            <div class="header-dropdown-menu" role="menu" aria-label="Navigation links">
              <a href="/boxbet/help" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 18h.01M9.1 9a3 3 0 1 1 5.8 1c-.57.78-1.55 1.23-2.18 2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Help
                </span>
              </a>
              <a href="/boxbet/create/" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                  Create Market
                </span>
              </a>
              <a href="/boxbet/adversary" class="header-dropdown-item" role="menuitem">
                <span class="header-dropdown-item-label">
                  <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4v5c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V7l8-4z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Adversary
                </span>
              </a>
            </div>
          </details>
          <div class="mono" style="font-size:.72rem; color:var(--muted);">${shortAddr(marketAddress)}</div>
        </div>
      </header>

      <section class="card" style="padding:16px; margin-bottom:12px; display:grid; gap:8px;">
        <div id="infoQuestion" style="font-size:1rem; font-weight:600;">Loading market...</div>
        <div style="display:grid; gap:6px;">
          <div class="odds-outcome" style="display:grid; gap:4px;">
            <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
              <div id="infoYesLabel" class="mono" style="display:flex; align-items:center; gap:8px; font-size:.76rem; padding:0; background:transparent; border:none; border-radius:0; box-shadow:none;">
                <img id="infoYesIcon" src="${import.meta.env.BASE_URL}images/win.svg" alt="-" style="width:20px; height:20px;" />
                <span>-</span>
              </div>
              <span id="infoYesPct" class="mono" style="font-size:.70rem; color:var(--text);">50.0%</span>
            </div>
            <div class="track" style="height:7px;"><div id="infoYesBar" class="fill yes" style="width:50%; height:7px;"></div></div>
          </div>
          <div class="odds-outcome" style="display:grid; gap:4px;">
            <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
              <div id="infoNoLabel" class="mono" style="display:flex; align-items:center; gap:8px; font-size:.76rem; padding:0; background:transparent; border:none; border-radius:0; box-shadow:none;">
                <img id="infoNoIcon" src="${import.meta.env.BASE_URL}images/out.svg" alt="-" style="width:20px; height:20px;" />
                <span>-</span>
              </div>
              <span id="infoNoPct" class="mono" style="font-size:.70rem; color:var(--text);">50.0%</span>
            </div>
            <div class="track" style="height:7px;"><div id="infoNoBar" class="fill no" style="width:50%; height:7px;"></div></div>
          </div>
        </div>
        <div id="infoReserve" class="mono" style="font-size:.72rem; color:var(--text);"></div>
        <div id="infoCollateral" class="mono" style="font-size:.72rem; color:var(--muted); display:flex; align-items:center; gap:6px;"></div>
      </section>

      <section class="card" style="padding:16px; display:grid; gap:10px;">
        <div class="analytics-range-row">
          <button id="infoRange12h" class="btn" type="button">12h</button>
          <button id="infoRange24h" class="btn btn-primary" type="button">24h</button>
          <button id="infoRange7d" class="btn" type="button">7d</button>
          <button
            id="infoRefresh"
            class="icon-btn"
            type="button"
            aria-label="Refresh analytics"
            title="Refresh analytics"
            style="background:transparent; border:none; box-shadow:none; padding:0; margin:0; color:#fff; display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; cursor:pointer;"
          >${REFRESH_ICON_IMG}</button>
        </div>

        <div id="infoSummary" class="mono" style="font-size:.72rem; color:var(--muted);"></div>

        <div class="analytics-chart-card">
          <div class="mono analytics-chart-title">Market Split</div>
          <div id="infoSplit"></div>
        </div>

        <div class="analytics-chart-card">
          <div class="mono analytics-chart-title">Window Net</div>
          <div id="infoNet"></div>
        </div>

        <div class="analytics-chart-card">
          <div class="mono analytics-chart-title">Buy / Sell</div>
          <div id="infoFlow"></div>
        </div>
      </section>
    </main>
  `;

  const el = {
    question: document.getElementById('infoQuestion') as HTMLDivElement,
    yesLabel: document.getElementById('infoYesLabel') as HTMLDivElement,
    noLabel: document.getElementById('infoNoLabel') as HTMLDivElement,
    yesIcon: document.getElementById('infoYesIcon') as HTMLImageElement,
    noIcon: document.getElementById('infoNoIcon') as HTMLImageElement,
    yesBar: document.getElementById('infoYesBar') as HTMLDivElement,
    noBar: document.getElementById('infoNoBar') as HTMLDivElement,
    yesPct: document.getElementById('infoYesPct') as HTMLSpanElement,
    noPct: document.getElementById('infoNoPct') as HTMLSpanElement,
    reserve: document.getElementById('infoReserve') as HTMLDivElement,
    collateral: document.getElementById('infoCollateral') as HTMLDivElement,
    summary: document.getElementById('infoSummary') as HTMLDivElement,
    split: document.getElementById('infoSplit') as HTMLDivElement,
    net: document.getElementById('infoNet') as HTMLDivElement,
    flow: document.getElementById('infoFlow') as HTMLDivElement,
    range12h: document.getElementById('infoRange12h') as HTMLButtonElement,
    range24h: document.getElementById('infoRange24h') as HTMLButtonElement,
    range7d: document.getElementById('infoRange7d') as HTMLButtonElement,
    refresh: document.getElementById('infoRefresh') as HTMLButtonElement,
  };

  const networkMainnetBtn = document.getElementById('networkMainnetBtn') as HTMLButtonElement;
  const networkTestnetBtn = document.getElementById('networkTestnetBtn') as HTMLButtonElement;
  const networkDropdown = document.getElementById('networkDropdown') as HTMLDetailsElement;
  const linksDropdown = document.getElementById('linksDropdown') as HTMLDetailsElement;

  function onNetworkToggle(next: NetworkKey): void {
    if (next === getActiveNetworkKey()) return;
    setActiveNetwork(next);
    window.location.reload();
  }

  function positionHeaderDropdown(dropdown: HTMLDetailsElement | null): void {
    if (!dropdown || !dropdown.open) return;
    const summary = dropdown.querySelector('summary') as HTMLElement | null;
    const menu = dropdown.querySelector('.header-dropdown-menu') as HTMLElement | null;
    if (!summary || !menu) return;
    const viewportPadding = 8;
    const summaryRect = summary.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const centeredLeft = summaryRect.left + (summaryRect.width / 2) - (menuRect.width / 2);
    const maxLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuRect.width);
    const clampedLeft = Math.min(Math.max(viewportPadding, centeredLeft), maxLeft);
    menu.style.left = `${clampedLeft - summaryRect.left}px`;
    menu.style.right = 'auto';
    menu.style.transform = 'none';
  }

  function refreshHeaderDropdownPositions(): void {
    positionHeaderDropdown(networkDropdown);
    positionHeaderDropdown(linksDropdown);
  }

  const scheduleHeaderDropdownRefresh = (): void => {
    window.requestAnimationFrame(refreshHeaderDropdownPositions);
  };

  networkMainnetBtn.addEventListener('click', () => onNetworkToggle('mainnet'));
  networkTestnetBtn.addEventListener('click', () => onNetworkToggle('testnet'));
  networkDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  linksDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  window.addEventListener('resize', refreshHeaderDropdownPositions);
  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (!networkDropdown.contains(target)) networkDropdown.open = false;
    if (!linksDropdown.contains(target)) linksDropdown.open = false;
  });

  let analyticsRange: AnalyticsRange = '24h';
  let yesOutcomeLabel = '-';
  let noOutcomeLabel = '-';
  let collateralLogoUrl = `${import.meta.env.BASE_URL}images/coin.svg`;
  let collateralAddress = DEPLOYMENT.collateral;
  let collateralMeta: CollateralMeta = defaultCollateralMeta(collateralAddress);
  let currentYesReserve = 0n;
  let currentNoReserve = 0n;
  let refreshTimer: number | null = null;
  let loading: Promise<void> | null = null;

  async function resolveBlockTimeLabel(blockNumber: bigint): Promise<string> {
    const cacheKey = `info:blockts:${getActiveNetworkKey()}:${blockNumber.toString()}`;
    const cached = getCache<string>(cacheKey, 31_536_000_000);
    if (cached) return cached;

    const block = await readClient.getBlock({ blockNumber });
    const label = new Date(Number(block.timestamp) * 1000).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    setCache(cacheKey, label);
    return label;
  }

  const analyticsBuckets = createAnalyticsBucketsService({
    resolveBlockTimeLabel,
    getViewportWidth: () => window.innerWidth || 1280,
  });

  function getIntervalMs(): number {
    return document.hidden ? INFO_REFRESH_HIDDEN_MS : INFO_REFRESH_VISIBLE_MS;
  }

  function updateRangeButtons(): void {
    const rows = [
      { btn: el.range12h, active: analyticsRange === '12h' },
      { btn: el.range24h, active: analyticsRange === '24h' },
      { btn: el.range7d, active: analyticsRange === '7d' },
    ];

    for (const row of rows) {
      row.btn.classList.toggle('btn-primary', row.active);
      row.btn.classList.toggle('btn', true);
      if (!row.active) row.btn.classList.remove('btn-primary');
    }
  }

  function formatAnalyticsValue(value: bigint): string {
    const usd = fmtCollateralUsd(value, collateralMeta);
    if (usd !== null) return `$${usd}`;
    return `${fmtCollateral(value, collateralMeta)} ${collateralMeta.symbol}`;
  }

  function formatAnalyticsSigned(value: bigint): string {
    const abs = value < 0n ? -value : value;
    return `${value >= 0n ? '+' : '-'}${formatAnalyticsValue(abs)}`;
  }

  async function refreshAnalytics(): Promise<void> {
    if (loading) {
      await loading;
      return;
    }

    loading = (async () => {
      updateRangeButtons();

      const [question, yesReserve, noReserve, collateral, yesToken, noToken] = await readContractsBatch<unknown>([
        { address: marketAddress, abi: marketAbi, functionName: 'question' },
        { address: marketAddress, abi: marketAbi, functionName: 'yesReserve' },
        { address: marketAddress, abi: marketAbi, functionName: 'noReserve' },
        { address: marketAddress, abi: marketAbi, functionName: 'collateral' },
        { address: marketAddress, abi: marketAbi, functionName: 'yesToken' },
        { address: marketAddress, abi: marketAbi, functionName: 'noToken' },
      ]);

      currentYesReserve = (yesReserve as bigint) || 0n;
      currentNoReserve = (noReserve as bigint) || 0n;
      const total = currentYesReserve + currentNoReserve;

      const rawCollateral = String(collateral || '').trim();
      if (/^0x[0-9a-fA-F]{40}$/.test(rawCollateral)) {
        collateralAddress = rawCollateral;
      }

      try {
        collateralMeta = await resolveCollateralMeta(collateralAddress);
      } catch {
        collateralMeta = defaultCollateralMeta(collateralAddress);
      }
      try {
        collateralLogoUrl = (await resolveTokenLogoUrl(collateralAddress)) || `${import.meta.env.BASE_URL}images/coin.svg`;
      } catch {
        collateralLogoUrl = `${import.meta.env.BASE_URL}images/coin.svg`;
      }

      let yesLabel = 'YES';
      let noLabel = 'NO';
      try {
        const yesName = yesToken
          ? await readClient.readContract({
            address: yesToken as Address,
            abi: erc20Abi,
            functionName: 'name',
          })
          : '-';
        yesLabel = normalizeOutcomeLabel(yesName, '-');
      } catch {
        yesLabel = '-';
      }
      try {
        const noName = noToken
          ? await readClient.readContract({
            address: noToken as Address,
            abi: erc20Abi,
            functionName: 'name',
          })
          : '-';
        noLabel = normalizeOutcomeLabel(noName, '-');
      } catch {
        noLabel = '-';
      }
      yesOutcomeLabel = yesLabel;
      noOutcomeLabel = noLabel;

      el.question.textContent = String(question || 'Untitled market');
      const yesPctRaw = total > 0n ? Number((currentYesReserve * 10_000n) / total) / 100 : 50;
      const noPctRaw = total > 0n ? Number((currentNoReserve * 10_000n) / total) / 100 : 50;
      const yesPctValue = Math.max(0, Math.min(100, yesPctRaw));
      const noPctValue = Math.max(0, Math.min(100, noPctRaw));
      const yesLeading = currentYesReserve >= currentNoReserve;
      const greenFilter = 'brightness(0) saturate(100%) invert(67%) sepia(64%) saturate(597%) hue-rotate(95deg) brightness(90%) contrast(89%)';
      const redFilter = 'brightness(0) saturate(100%) invert(51%) sepia(42%) saturate(854%) hue-rotate(302deg) brightness(95%) contrast(89%)';

      const winIcon = `${import.meta.env.BASE_URL}images/win.svg`;
      const loseIcon = `${import.meta.env.BASE_URL}images/out.svg`;
      el.yesIcon.src = yesLeading ? winIcon : loseIcon;
      el.noIcon.src = yesLeading ? loseIcon : winIcon;
      el.yesIcon.style.filter = yesLeading ? greenFilter : redFilter;
      el.noIcon.style.filter = yesLeading ? redFilter : greenFilter;
      el.yesIcon.alt = yesOutcomeLabel;
      el.noIcon.alt = noOutcomeLabel;
      el.yesLabel.querySelector('span')!.textContent = yesOutcomeLabel;
      el.noLabel.querySelector('span')!.textContent = noOutcomeLabel;
      el.yesPct.textContent = `${yesPctValue.toFixed(2)}%`;
      el.noPct.textContent = `${noPctValue.toFixed(2)}%`;
      el.yesBar.style.width = `${yesPctValue}%`;
      el.noBar.style.width = `${noPctValue}%`;
      el.yesBar.classList.remove('yes', 'no');
      el.noBar.classList.remove('yes', 'no');
      el.yesBar.classList.add(yesLeading ? 'yes' : 'no');
      el.noBar.classList.add(yesLeading ? 'no' : 'yes');
      el.yesLabel.classList.remove('market-share-green', 'market-share-red');
      el.noLabel.classList.remove('market-share-green', 'market-share-red');
      el.yesPct.classList.remove('market-share-green', 'market-share-red');
      el.noPct.classList.remove('market-share-green', 'market-share-red');
      el.yesLabel.classList.add(yesLeading ? 'market-share-green' : 'market-share-red');
      el.noLabel.classList.add(yesLeading ? 'market-share-red' : 'market-share-green');
      el.yesPct.classList.add(yesLeading ? 'market-share-green' : 'market-share-red');
      el.noPct.classList.add(yesLeading ? 'market-share-red' : 'market-share-green');

      el.reserve.textContent = `${yesOutcomeLabel} ${fmtCollateral(currentYesReserve, collateralMeta)} / ${noOutcomeLabel} ${fmtCollateral(currentNoReserve, collateralMeta)}`;
      const collateralMetaPending = collateralMeta.symbol === 'TOKEN' || collateralMeta.name === 'Collateral Token';
      el.collateral.innerHTML = collateralMetaPending
        ? `<img src="${collateralLogoUrl}" alt="Collateral" class="token-logo"/> Collateral: -`
        : `<img src="${collateralLogoUrl}" alt="${collateralMeta.symbol}" class="token-logo"/> Collateral: ${collateralMeta.name} (${collateralMeta.symbol})`;

      const history = await getMarketHistory(marketAddress, currentYesReserve, currentNoReserve, collateralAddress);
      if (history.length === 0) {
        el.summary.textContent = 'No trade history yet for this market.';
        el.split.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No probability history yet.</div>';
        el.net.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No net flow history yet.</div>';
        el.flow.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No buy/sell history yet.</div>';
        return;
      }

      const windowMs = analyticsRange === '12h'
        ? 12 * 60 * 60 * 1000
        : analyticsRange === '24h'
          ? 24 * 60 * 60 * 1000
          : 7 * 24 * 60 * 60 * 1000;
      const windowStart = Date.now() - windowMs;
      const visibleHistory = history.filter((point) => point.timestampMs >= windowStart);

      if (visibleHistory.length === 0) {
        el.summary.textContent = `No trades found in the last ${analyticsRange}.`;
        el.split.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No probability history in this range.</div>';
        el.net.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No net flow in this range.</div>';
        el.flow.innerHTML = '<div class="mono" style="font-size:.72rem; color:var(--muted);">No buy/sell history in this range.</div>';
        return;
      }

      const buckets = await analyticsBuckets.getLabeledBucketsForRange(analyticsRange, visibleHistory);
      const recentWindow = buckets.slice(-Math.min(4, buckets.length));
      const recentNetFlow = recentWindow.reduce((acc, point) => acc + point.netFlow, 0n);
      const recentTone = recentNetFlow >= 0n ? 'var(--green)' : 'var(--red)';
      const latestPoint = buckets[buckets.length - 1];
      const firstPoint = buckets[0];
      const latestYesProbability = typeof latestPoint?.yesProbability === 'number' && Number.isFinite(latestPoint.yesProbability)
        ? latestPoint.yesProbability
        : 50;
      const latestNoProbability = typeof latestPoint?.noProbability === 'number' && Number.isFinite(latestPoint.noProbability)
        ? latestPoint.noProbability
        : 50;
      const firstYesProbability = typeof firstPoint?.yesProbability === 'number' && Number.isFinite(firstPoint.yesProbability)
        ? firstPoint.yesProbability
        : latestYesProbability;
      const splitMove = latestYesProbability - firstYesProbability;
      const splitMoveLabel = `${splitMove >= 0 ? '+' : ''}${splitMove.toFixed(2)} pts`;
      const momentumLabel = recentNetFlow > 0n ? yesOutcomeLabel : recentNetFlow < 0n ? noOutcomeLabel : 'Balanced';

      const windowFlowSeries = [{
        label: analyticsRange,
        yesBuyFlow: visibleHistory.reduce((acc, point) => acc + point.yesBuyFlow, 0n),
        yesSellFlow: visibleHistory.reduce((acc, point) => acc + point.yesSellFlow, 0n),
        noBuyFlow: visibleHistory.reduce((acc, point) => acc + point.noBuyFlow, 0n),
        noSellFlow: visibleHistory.reduce((acc, point) => acc + point.noSellFlow, 0n),
      }];
      const selectedWindow = windowFlowSeries[0];
      const totalBuyFlow = selectedWindow.yesBuyFlow + selectedWindow.noBuyFlow;
      const totalSellFlow = selectedWindow.yesSellFlow + selectedWindow.noSellFlow;
      const totalNetFlow = totalBuyFlow - totalSellFlow;
      const totalVolume = totalBuyFlow + totalSellFlow;

      const traderVolumeByAddress = new Map<string, bigint>();
      for (const point of visibleHistory) {
        const trader = String(point.trader || '').toLowerCase();
        if (!trader || !point.tradeVolume) continue;
        traderVolumeByAddress.set(trader, (traderVolumeByAddress.get(trader) ?? 0n) + point.tradeVolume);
      }
      const sortedTraderVolume = Array.from(traderVolumeByAddress.values()).sort((a, b) => (a > b ? -1 : a < b ? 1 : 0));
      const topTraderVolume = sortedTraderVolume[0] ?? 0n;
      const top3Volume = sortedTraderVolume.slice(0, 3).reduce((acc, value) => acc + value, 0n);
      const topTraderShare = totalVolume > 0n ? Number((topTraderVolume * 10_000n) / totalVolume) / 100 : 0;
      const top3Share = totalVolume > 0n ? Number((top3Volume * 10_000n) / totalVolume) / 100 : 0;
      const concentrationTone = topTraderShare >= 45 ? '#ff9b9b' : topTraderShare >= 30 ? '#f6c66e' : '#97e6bf';

      const tvlBalance = (await readClient.readContract({
        address: collateralAddress as Address,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [marketAddress],
      })) as bigint;
      const currentTvl = (tvlBalance as bigint) || 0n;

      const flowTone = totalNetFlow >= 0n ? 'var(--green)' : 'var(--red)';
      const yesWindowNet = selectedWindow.yesBuyFlow - selectedWindow.yesSellFlow;
      const noWindowNet = selectedWindow.noBuyFlow - selectedWindow.noSellFlow;
      const summaryStrip = renderSummaryPills([
        { label: 'Volume', value: formatAnalyticsValue(totalVolume), tone: '#8ec9ff' },
        { label: 'TVL', value: formatAnalyticsValue(currentTvl), tone: '#f6c66e' },
        { label: 'Net', value: `${totalNetFlow >= 0n ? '+' : '-'}${formatAnalyticsValue(totalNetFlow < 0n ? -totalNetFlow : totalNetFlow)}`, tone: flowTone },
        { label: 'Top Trader', value: `${topTraderShare.toFixed(1)}%`, tone: concentrationTone },
        { label: 'Top 3 Share', value: `${top3Share.toFixed(1)}%`, tone: concentrationTone },
        { label: 'Trend', value: momentumLabel, tone: recentTone },
        { label: 'Move', value: splitMoveLabel, tone: splitMove >= 0 ? 'var(--green)' : 'var(--red)' },
      ]);

      const yesIsTrending = latestYesProbability >= latestNoProbability;
      const yesTone = yesIsTrending ? '#2ec989' : '#e8637a';
      const noTone = yesIsTrending ? '#e8637a' : '#2ec989';
      const topTradesMarkup = renderTopTrades(visibleHistory, formatAnalyticsValue, 5);

      el.summary.innerHTML = [
        `Volume ${formatAnalyticsValue(totalVolume)}`,
        `TVL ${formatAnalyticsValue(currentTvl)}`,
        `Net ${totalNetFlow >= 0n ? '+' : '-'}${formatAnalyticsValue(totalNetFlow < 0n ? -totalNetFlow : totalNetFlow)}`,
      ].join(' · ');

      el.split.innerHTML = `
        ${summaryStrip}
        <div class="mono analytics-card-copy">Latest split: <span style="color:${yesTone}">${latestYesProbability.toFixed(2)}% ${yesOutcomeLabel}</span> / <span style="color:${noTone}">${latestNoProbability.toFixed(2)}% ${noOutcomeLabel}</span></div>
        ${renderFlowSplitSvg(
          buckets.map((point) => ({
            label: point.label,
            yesBuyFlow: point.yesBuyFlow,
            noBuyFlow: point.noBuyFlow,
            yesSellFlow: point.yesSellFlow,
            noSellFlow: point.noSellFlow,
          })),
          (value: bigint) => fmtCollateral(value, collateralMeta),
        )}
      `;

      el.net.innerHTML = `
        <div class="mono analytics-card-copy">${analyticsRange} window net: <span style="color:${yesTone}">${yesOutcomeLabel} ${formatAnalyticsSigned(yesWindowNet)}</span> / <span style="color:${noTone}">${noOutcomeLabel} ${formatAnalyticsSigned(noWindowNet)}</span></div>
        ${renderBarHistorySvg(windowFlowSeries, {
          yesOutcomeLabel,
          noOutcomeLabel,
          formatValue: formatAnalyticsValue,
          formatSigned: formatAnalyticsSigned,
          yesIsTrending,
        })}
      `;

      el.flow.innerHTML = `
        <div class="mono analytics-card-copy">${analyticsRange} buy/sell activity: <span style="color:${yesTone}">${yesOutcomeLabel}</span> and <span style="color:${noTone}">${noOutcomeLabel}</span>.</div>
        ${renderStackedFlowSvg(windowFlowSeries, {
          yesOutcomeLabel,
          noOutcomeLabel,
          formatValue: formatAnalyticsValue,
          formatSigned: formatAnalyticsSigned,
          yesIsTrending,
        })}
        ${topTradesMarkup}
      `;
    })();

    try {
      await loading;
    } finally {
      loading = null;
    }
  }

  function scheduleRefresh(nextDelayMs?: number): void {
    if (refreshTimer !== null) {
      window.clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    const waitMs = typeof nextDelayMs === 'number' ? Math.max(3_000, nextDelayMs) : getIntervalMs();
    refreshTimer = window.setTimeout(() => {
      void refreshAnalytics().finally(() => {
        scheduleRefresh();
      });
    }, waitMs);
  }

  function onVisibilityOrFocusChange(): void {
    scheduleRefresh();
  }

  el.range12h.addEventListener('click', () => {
    analyticsRange = '12h';
    void refreshAnalytics();
  });

  el.range24h.addEventListener('click', () => {
    analyticsRange = '24h';
    void refreshAnalytics();
  });

  el.range7d.addEventListener('click', () => {
    analyticsRange = '7d';
    void refreshAnalytics();
  });

  el.refresh.addEventListener('click', () => {
    el.refresh.classList.remove('is-spinning');
    void el.refresh.offsetWidth;
    el.refresh.classList.add('is-spinning');
    void refreshAnalytics();
  });
  el.refresh.addEventListener('animationend', (event) => {
    if (event.animationName !== 'refresh-spin-once') return;
    el.refresh.classList.remove('is-spinning');
  });

  document.addEventListener('visibilitychange', onVisibilityOrFocusChange);
  window.addEventListener('focus', onVisibilityOrFocusChange);
  window.addEventListener('blur', onVisibilityOrFocusChange);
  window.addEventListener('beforeunload', () => {
    if (refreshTimer !== null) {
      window.clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  });

  await refreshAnalytics();
  scheduleRefresh(getIntervalMs());
}
